/**
 * The project's Traces tab (design "Traces"): how the project's services talk to each other, from the requests that
 * went through the preview. On top the range (Last 5 minutes / 15 minutes / hour), the counts (requests, failed, p95)
 * and Pause; then the service map (ServiceMap), the requests per minute and, on the right (below, on a phone), the
 * requests: found by correlationId, path or service, filtered All / Failed / Slow. A request opens in place (TraceView),
 * kept in the URL (`&trace=<id>`), with "← Traces" back. Refreshed every 5 s while the page is visible, not paused and no
 * request is open; a request new in such a refresh flies over the map (mapFlights.js). The list is newest first. Its links: the code at a line (the Files view, as the chat's file links do), a store in Data
 * (`onOpenView({ tab: 'data', focus })`), an endpoint in API (only when this build has that tab) and the project chat
 * (`onAskAI(prompt, { prefill: true })`, as the Data tab does). A request that never reached its service (the preview
 * proxy answered by itself) names that service in the list ("app · not reached") and, opened, offers its logs
 * (`onOpenLogs(service, { at })`) and Start / Restart when it is stopped or crashed (ServiceActions, as everywhere).
 * A switch on top shows, instead of the map and the requests, the services' OpenTelemetry log records (LogsView, a record
 * of a request opens it here) or their metrics (MetricsView), over the same range.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Pause, Play } from '@phosphor-icons/react';
import ServiceMap from './ServiceMap';
import ServiceActions from '../sandbox/ServiceActions';
import useProjectServices from '../../hooks/useProjectServices';
import { canonicalServiceName, serviceState } from '../../services/SandboxService';
import TraceView from './TraceView';
import LogsView from './LogsView';
import MetricsView from './MetricsView';
import { VIEWS } from './signalsModel';
import { getMap, getTrace, listTraces } from '../../services/TracesService';
import { VIEW_TABS } from '../../utils/workspaceModel';
import { formatMs } from './waterfallLayout';
import { MAX_FLIGHTS, pickNewTraces, sortNewest, tracePath } from './mapFlights';
import {
  DEFAULT_RANGE, LIST_LIMIT, RANGES, REFRESH_MS, askPrompt, bucketText, chartDescription, codeSearch, fullTime, isoTime, listTime, outcomeOf, plural, rowServices,
  searchWithTrace, shortId, statusOf, targetControl, traceFromSearch,
} from './tracesModel';
import './Traces.css';

const errorText = (error) => error?.body?.error || error?.message || String(error || 'Something went wrong');
const HAS_API_TAB = VIEW_TABS.includes('api');

function writeTraceToUrl(traceId) {
  if (typeof window === 'undefined') { return; }
  const next = searchWithTrace(window.location.search, traceId);
  if (next !== window.location.search) {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${next}${window.location.hash}`);
  }
}

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function State({ title, children = null, tone = null, action = null }) {
  return (
    <div className={`traces-state${tone ? ` is-${tone}` : ''}`} role={tone === 'bad' ? 'alert' : undefined}>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

State.propTypes = { action: PropTypes.node, children: PropTypes.node, title: PropTypes.node.isRequired, tone: PropTypes.string };

function RateChart({ perMinute, ago, rangeLabel, bucketMs = 60000, keptNote = null }) {
  const peak = Math.max(1, ...perMinute.map((b) => b.total || 0));
  return (
    <section aria-labelledby="traces-rate-title" className="traces-card traces-rate">
      <h2 id="traces-rate-title">Requests per {bucketText(bucketMs)} <span className="traces-muted-inline">· red: failed</span></h2>
      {keptNote && <p className="traces-muted-inline" data-testid="traces-kept">{keptNote}</p>}
      <div aria-label={chartDescription(perMinute, rangeLabel, bucketMs)} className="traces-bars" data-testid="traces-rate" role="img">
        {perMinute.map((b) => {
          const total = b.total || 0;
          const failed = Math.min(total, b.failed || 0);
          return (
            <span className="traces-bar-col" key={b.t} title={`${new Date(b.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}: ${plural(total, 'request')}${failed ? `, ${failed} failed` : ''}`}>
              <span className="traces-bar-ok" style={{ height: `${((total - failed) / peak) * 100}%` }} />
              {failed > 0 && <span className="traces-bar-bad" style={{ height: `${(failed / peak) * 100}%` }} />}
            </span>
          );
        })}
      </div>
      <div aria-hidden className="traces-bars-axis"><span>{ago}</span><span>now</span></div>
    </section>
  );
}

RateChart.propTypes = { ago: PropTypes.string.isRequired, rangeLabel: PropTypes.string.isRequired, bucketMs: PropTypes.number, keptNote: PropTypes.string, perMinute: PropTypes.array.isRequired };

/**
 * Start / Restart for the service a request never reached, when it is stopped, failed or crashed now (the project's
 * services, as the pod it runs in reports them); nothing for a viewer or while it runs or builds.
 */
function TargetActions({ projectId, service, stateAtFailure = null }) {
  const serviceName = canonicalServiceName(service);
  const { services, canRun, loaded, refresh } = useProjectServices(projectId, { intervalMs: 5000 });
  if (!loaded || !canRun) { return null; }
  const entry = services.find((s) => s.serviceName === serviceName) || null;
  const control = targetControl(entry ? serviceState(entry) : (stateAtFailure === 'crashed' ? 'crashed' : 'stopped'));
  if (!control) { return null; }
  return (
    <span className="traces-target-actions" data-testid="traces-target-actions">
      <ServiceActions onChanged={refresh} projectId={projectId} service={entry} serviceName={serviceName} showRemove={false} />
    </span>
  );
}

TargetActions.propTypes = { projectId: PropTypes.string.isRequired, service: PropTypes.string.isRequired, stateAtFailure: PropTypes.string };

function RequestsList({ list, find, onFind, status, onStatus, slowMs, onOpen, openId }) {
  const counts = list?.counts || {};
  const traces = useMemo(() => sortNewest(list?.traces || []), [list]);
  const now = Date.now();
  const filters = [
    { id: 'all', label: 'All' },
    { id: 'failed', label: `Failed ${counts.failed ?? 0}`, bad: true },
    { id: 'slow', label: `Slow > ${slowMs} ms` },
  ];
  return (
    <section aria-labelledby="traces-list-title" className="traces-card traces-list">
      <div className="traces-list-head">
        <h2 id="traces-list-title">Requests</h2>
        <label className="traces-sr" htmlFor="traces-find">Find by correlationId</label>
        <input
          className="traces-input traces-mono"
          id="traces-find"
          onChange={(e) => onFind(e.target.value)}
          placeholder="correlationId, path, service…"
          type="search"
          value={find}
        />
        <div aria-label="Show" className="traces-filters" role="group">
          {filters.map((f) => (
            <button
              aria-pressed={status === f.id}
              className={`traces-pill is-toggle${f.bad ? ' is-bad-text' : ''}`}
              data-testid={`traces-filter-${f.id}`}
              key={f.id}
              onClick={() => onStatus(f.id)}
              type="button"
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div aria-hidden className="traces-row is-head"><span>correlationId · entry</span><span>services</span><span>time</span><span>status</span></div>
      {list && !traces.length && (
        <p className="traces-muted traces-list-empty">{find.trim() || status !== 'all' ? 'No request matches.' : 'No requests in this range.'}</p>
      )}
      <ul aria-label="Requests found" className="traces-rows">
        {traces.map((t) => {
          const s = statusOf(t);
          const id = t.correlationId || t.traceId;
          return (
            <li key={t.traceId}>
              <button
                aria-current={openId === t.traceId ? 'true' : undefined}
                className="traces-row"
                data-testid={`traces-row-${t.traceId}`}
                onClick={() => onOpen(t.traceId)}
                title={`correlationId ${id}`}
                type="button"
              >
                <span>
                  <span className="traces-mono">{shortId(id)}</span>
                  {t.start ? <time className="traces-row-time" dateTime={isoTime(t.start)} title={fullTime(t.start)}>{listTime(t.start, now)}</time> : null}
                  <br />{t.entry}
                </span>
                {(() => {
                  const where = rowServices(t);
                  return <span className={`traces-row-services${where.bad ? ' is-bad-text' : ''}`} title={where.title}>{where.text}</span>;
                })()}
                <span>{formatMs(t.durationMs)}</span>
                <span><span className={`traces-pill is-${s.tone}`}>{s.text}</span></span>
              </button>
            </li>
          );
        })}
      </ul>
      {list?.total > traces.length && <p className="traces-muted traces-list-empty">The newest {traces.length} of {list.total}: search to find the others.</p>}
      <p className="traces-footnote">Each request keeps one correlationId across services: Flow adds it at the entry (x-correlation-id / traceparent) and every service and message carries it on.</p>
    </section>
  );
}

RequestsList.propTypes = {
  find: PropTypes.string.isRequired,
  list: PropTypes.object,
  onFind: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onStatus: PropTypes.func.isRequired,
  openId: PropTypes.string,
  slowMs: PropTypes.number,
  status: PropTypes.string.isRequired,
};

const ALL_TABS = () => true;

export default function TracesPanel({ projectId, onAskAI = null, onOpenView = null, onOpenLogs = null, shows = ALL_TABS, focus = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [range, setRange] = useState(DEFAULT_RANGE);
  // Requests (the map and the list), Logs or Metrics.
  const [view, setView] = useState('requests');
  const [paused, setPaused] = useState(false);
  // Opened from a service's page (Apps): its requests first.
  const [find, setFind] = useState(() => (focus?.service ? String(focus.service).replace(/^services\//, '') : ''));
  const [status, setStatus] = useState('all');
  const [map, setMap] = useState(null);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [traceId, setTraceId] = useState(() => (typeof window === 'undefined' ? null : traceFromSearch(window.location.search)));
  const [trace, setTrace] = useState(null);
  const [traceError, setTraceError] = useState(null);
  const q = useDebounced(find.trim(), 300);
  const seq = useRef(0);
  const seenRef = useRef(null);
  const [arrivals, setArrivals] = useState(null);

  // New requests of a live refresh fly over the map: at most 3, their paths from their steps (≤ 3 extra calls).
  const fly = useCallback(async (ids, nextMap) => {
    const answers = await Promise.all(ids.map((id) => getTrace(projectId, id).catch(() => null)));
    const flights = answers.filter(Boolean).map((t) => ({ key: t.traceId, hops: tracePath(t, nextMap) })).filter((f) => f.hops.length);
    if (flights.length) { setArrivals(flights); }
  }, [projectId]);

  const load = useCallback(async ({ live = false } = {}) => {
    const mine = ++seq.current;
    try {
      const [nextMap, nextList] = await Promise.all([
        getMap(projectId, { since: range }),
        listTraces(projectId, { since: range, q, status, limit: LIST_LIMIT }),
      ]);
      if (mine !== seq.current) { return; }
      setMap(nextMap);
      setList(nextList);
      setError(null);
      // What is new since the last refresh of the same range and search (nothing on a first load or a change).
      const picked = pickNewTraces(seenRef.current, `${range}|${q}|${status}`, nextList?.traces || [], MAX_FLIGHTS);
      seenRef.current = picked.memory;
      if (live && picked.fresh.length && nextMap?.enabled !== false) { fly(picked.fresh, nextMap); }
    } catch (e) {
      if (mine === seq.current) { setError(e); }
    }
  }, [projectId, range, q, status, fly]);

  useEffect(() => { load(); }, [load]);

  // Every 5 s, while someone looks: not paused, no request open, the page visible.
  useEffect(() => {
    if (paused || traceId || view !== 'requests') { return undefined; }
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) { return; }
      load({ live: true });
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, paused, traceId, view]);

  useEffect(() => {
    if (!traceId) { setTrace(null); setTraceError(null); return undefined; }
    let live = true;
    setTrace(null);
    setTraceError(null);
    getTrace(projectId, traceId)
      .then((answer) => { if (live) { setTrace(answer); } })
      .catch((e) => { if (live) { setTraceError(e); } });
    return () => { live = false; };
  }, [projectId, traceId]);

  const openTrace = (id) => { setTraceId(id); writeTraceToUrl(id); };
  const closeTrace = () => { setTraceId(null); writeTraceToUrl(null); };

  const ask = (t, row) => onAskAI?.(askPrompt(t, row), { prefill: true });
  const openCode = (code) => navigate(`${location.pathname}${codeSearch(window.location.search, code)}`);
  const openData = (focus) => onOpenView?.({ tab: 'data', focus });
  const openApi = (endpoint) => onOpenView?.({ tab: 'api', focus: { endpoint } });

  const rangeInfo = RANGES.find((r) => r.value === range) || RANGES[1];
  const rangeLabel = rangeInfo.label;
  // A busy project's oldest requests go first (the gateway keeps a bounded number): say from when this range is complete.
  const keptNote = map?.keptSince && map?.since && map.keptSince > map.since + (map.bucketMs || 60000)
    ? `Requests kept since ${new Date(map.keptSince).toLocaleString('en', { dateStyle: 'medium', timeStyle: 'short' })}.` : null;
  const totals = map?.totals || {};
  const slowMs = totals.slowMs || 500;
  const perMinute = useMemo(() => map?.perMinute || [], [map]);

  if (traceId) {
    let body;
    if (traceError) {
      body = traceError.code === 'not_found' || (traceError.status === 404 && traceError.code !== 'sandbox_outdated')
        ? <State title="This request is no longer kept">Traces keep the last hour of requests. Go back to the list to pick a newer one.</State>
        : <State title="Could not open this request" tone="bad">{errorText(traceError)}</State>;
    } else if (!trace) {
      body = <div className="traces-state" role="status">Opening the request…</div>;
    }
    return (
      <div className="traces-panel" data-testid="traces-panel">
        {trace && !traceError ? (
          <TraceView
            key={trace.traceId}
            onAsk={onAskAI ? (row) => ask(trace, row) : null}
            onBack={closeTrace}
            onOpenApi={HAS_API_TAB && onOpenView && shows('api') ? openApi : null}
            onOpenCode={shows('code') ? openCode : null}
            onOpenData={onOpenView && shows('data') ? openData : null}
            onOpenLogs={onOpenLogs ? (service, at) => onOpenLogs(canonicalServiceName(service), at) : null}
            targetActions={outcomeOf(trace)?.kind === 'unreached'
              ? <TargetActions projectId={projectId} service={outcomeOf(trace).service} stateAtFailure={outcomeOf(trace).target?.state || null} />
              : null}
            trace={trace}
          />
        ) : (
          <div className="traces-trace">
            <button className="traces-back" onClick={closeTrace} type="button"><span aria-hidden>← </span><span className="traces-sr">Back to </span>Traces</button>
            {body}
          </div>
        )}
      </div>
    );
  }

  let main;
  if (view === 'logs') {
    main = <LogsView onOpenTrace={openTrace} paused={paused} projectId={projectId} range={range} />;
  } else if (view === 'metrics') {
    main = <MetricsView paused={paused} projectId={projectId} range={range} rangeLabel={rangeLabel} />;
  } else if (error && !map) {
    main = (
      <State action={<button className="traces-btn" onClick={() => load()} type="button">Try again</button>} title="Could not load the traces" tone="bad">
        {errorText(error)}
      </State>
    );
  } else if (!map) {
    main = <div className="traces-state" role="status">Loading the requests…</div>;
  } else if (map.enabled === false) {
    main = <State title="Tracing is off for this sandbox">{map.reason || 'The services of this sandbox do not send traces.'}</State>;
  } else {
    const empty = !(map.nodes || []).length && !totals.requests;
    main = (
      <div className="traces-grid">
        <div className="traces-left">
          {empty ? (
            <section aria-label="Service map" className="traces-card">
              <State title="No requests yet in this range">
                Open the Preview and use the app: every request through the preview shows here, with the services it crossed.
              </State>
            </section>
          ) : <ServiceMap arrivals={arrivals} map={map} onPick={setFind} picked={find.trim()} />}
          <RateChart ago={rangeInfo.ago} rangeLabel={rangeLabel} bucketMs={map?.bucketMs || 60000} keptNote={keptNote} perMinute={perMinute} />
        </div>
        <RequestsList find={find} list={list} onFind={setFind} onOpen={openTrace} onStatus={setStatus} openId={traceId} slowMs={slowMs} status={status} />
      </div>
    );
  }

  const enabled = map && map.enabled !== false && view === 'requests';
  return (
    <div className="traces-panel" data-testid="traces-panel">
      <div className="traces-head">
        <div aria-label="View" className="traces-filters traces-views" role="group">
          {VIEWS.map((v) => (
            <button aria-pressed={view === v.id} className="traces-pill is-toggle" data-testid={`traces-view-${v.id}`} key={v.id} onClick={() => setView(v.id)} type="button">
              {v.label}
            </button>
          ))}
        </div>
        <h1>{view === 'requests' ? 'Service map' : VIEWS.find((v) => v.id === view).label}</h1>
        <label className="traces-sr" htmlFor="traces-range">Time range</label>
        <select className="traces-btn" id="traces-range" onChange={(e) => setRange(e.target.value)} value={range}>
          {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {enabled && (
          <span aria-live="polite" className="traces-counts" data-testid="traces-counts">
            <span className="traces-pill">{plural(totals.requests || 0, 'request')}</span>
            <span className={`traces-pill${totals.failed ? ' is-bad' : ''}`}>{totals.failed || 0} failed</span>
            {totals.p95Ms !== undefined && totals.p95Ms !== null && <span className="traces-pill">p95 {formatMs(totals.p95Ms)}</span>}
          </span>
        )}
        <button
          className="traces-btn"
          onClick={() => setPaused((p) => !p)}
          title={paused ? 'Refresh every 5 seconds again' : 'Stop refreshing every 5 seconds'}
          type="button"
        >
          {paused ? <Play aria-hidden size={14} /> : <Pause aria-hidden size={14} />}
          {paused ? 'Resume' : 'Pause'}
        </button>
        {error && map && view === 'requests' && <span className="traces-muted" role="status">Could not refresh: {errorText(error)}</span>}
      </div>
      {main}
    </div>
  );
}

TracesPanel.propTypes = {
  focus: PropTypes.shape({ service: PropTypes.string }),
  // (prompt, { prefill: true }) => void: writes the prompt into the project chat (App's askAIInProjectChat).
  onAskAI: PropTypes.func,
  // (service, { at }) => void: the Logs view on that service (`services/app`), at that time (epoch ms).
  onOpenLogs: PropTypes.func,
  // (view) => void: opens another workspace view (Data on a store, API on an endpoint).
  onOpenView: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  // (tab) => boolean: whether the person's plan shows a tab (hooks/usePlanFeatures usePlanTab); its links only then.
  shows: PropTypes.func,
};
